/**
 * Strategy Intelligence Summary — Trading UI client (Phase 12BJ).
 *
 * Single GET that aggregates AI Stack, Signal lanes, Conviction Tracker,
 * market-news freshness, Council readiness, and Tutor suggestions. Same-
 * origin /api/v1; no admin diagnostics; no raw JSON dump.
 */
import { apiV1Base } from "./apiConfig";

export interface SISignalAIStack {
  symbol: string;
  primary_layer: string;
  ai_stack_score: number;
  posture: string;
  deployment_status: string;
  change_pct: number | null;
  catalyst_status: string;
}

export interface SISignalLane {
  id: string;
  label: string;
  count: number;
  data_status: string;
  description: string;
}

export interface SIConvictionSignal {
  symbol: string;
  conviction_score: number;
  action_posture: string;
  copycat_risk: string;
  themes: string[];
}

export interface StrategyIntelligenceSummary {
  status: "OK";
  schema_version: number;
  generated_at: string;
  source_health: Record<string, Record<string, unknown>>;
  ai_stack: {
    status: string;
    signal_count: number;
    top_signals: SISignalAIStack[];
    memory_storage_spotlight: SISignalAIStack[];
    top_postures: Record<string, number>;
    catalyst_provider_state: string | null;
    catalyst_source_age_seconds: number | null;
    catalyst_source?: string | null;
    limitations: string[];
  };
  matrix: {
    status: string;
    schema_version: number | null;
    lanes: SISignalLane[];
    phase13_isolation_note?: string;
    limitations: string[];
  };
  conviction_tracker: {
    status: string;
    provider_state: string;
    signal_count: number;
    disclosure_count: number;
    top_signals: SIConvictionSignal[];
    political_oge_status: string;
    limitations: string[];
  };
  market_news: {
    status: string;
    provider_state: string;
    source_age_seconds: number | null;
    providers_succeeded: string[];
    providers_failed: string[];
    item_count: number;
    symbols_covered: number;
    last_updated: string | null;
  };
  council: {
    status: string;
    backend_cascade: boolean;
    decision_support_only: boolean;
    ai_stack_prompt_context_gate: "ON" | "OFF";
    available_context_modules: string[];
    limitations: string[];
  };
  tutor: {
    status: string;
    backend_cascade: boolean;
    knowledge_modules: string[];
    suggested_questions: string[];
    refusal_active: boolean;
  };
  limitations: string[];
}

type Envelope<T> = { ok: boolean; data: T | null; error?: { message: string; code: string } | null };

export async function fetchStrategyIntelligenceSummary(): Promise<Envelope<StrategyIntelligenceSummary>> {
  try {
    const res = await fetch(`${apiV1Base()}/strategy-intelligence/summary`, {
      headers: { accept: "application/json" },
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) {
      return { ok: false, data: null, error: { message: `HTTP ${res.status}`, code: String(res.status) } };
    }
    return (await res.json()) as Envelope<StrategyIntelligenceSummary>;
  } catch (err: any) {
    return { ok: false, data: null, error: { message: err?.message || "network", code: "NETWORK" } };
  }
}
